export type Experience = {
  period: string;
  company: string;
  role: string;
  context?: string;
  scope: string[];
};

export type SkillGroup = {
  title: string;
  items: string[];
};

export const profile = {
  name: "Oleksii Golub",
  role: "Principal Delivery Manager",
  headline: "Portfolio delivery leader for cloud transformation, AI-powered solutions, customer success, and data platforms.",
  location: "Atlanta, USA",
  linkedin: "https://www.linkedin.com/in/oleksiiholub/",
  cvUrl: "/files/oleksii-golub-cv.docx",
  summary: [
    "Solutions-oriented software development and delivery leader with 10+ years of technology experience.",
    "Leads global teams, cloud transformation, AI-powered solutions, customer success, operational excellence, and executive stakeholder alignment.",
    "Directed delivery portfolios from $5M to $20M annually, with emphasis on scalable governance, measurable business outcomes, and durable partnerships."
  ],
  metrics: [
    { value: "10+", label: "years in technology delivery" },
    { value: "$5M-$20M", label: "annual portfolio ownership" },
    { value: "250+", label: "interns graduated annually" },
    { value: "500%", label: "department revenue growth at Art Lemon" }
  ],
  proofPoints: [
    "Program director for multi-brand restaurant holding initiatives across data engineering, big data, DevOps, platform, SRE, and data science.",
    "Built delivery systems spanning P&L, budget control, staffing, roadmap planning, risk, governance, and executive reporting.",
    "Managed global internship operations and conversion pipeline in partnership with HR, technical leadership, and marketing.",
    "Scaled international business development, partnerships, and dedicated team models for digital agency clients."
  ]
};

export const skills: SkillGroup[] = [
  {
    title: "Delivery Leadership",
    items: [
      "IT service delivery",
      "Program and portfolio management",
      "PMO",
      "SLA and KPI management",
      "Risk and issue governance",
      "Business continuity planning"
    ]
  },
  {
    title: "Customer and Operations",
    items: [
      "Stakeholder management",
      "Customer success",
      "NPS improvement",
      "Vendor and contract management",
      "Multi-vendor environments",
      "ITIL-based managed services"
    ]
  },
  {
    title: "Transformation Platforms",
    items: [
      "Cloud strategy",
      "Azure and AWS",
      "AI and ML analytics",
      "OpenAI and Gemini",
      "Power BI and Tableau",
      "Snowflake and Databricks"
    ]
  },
  {
    title: "Governance",
    items: [
      "ISO and SOX controls",
      "Data governance",
      "Enterprise strategy",
      "P&L management",
      "Resource planning",
      "Lean-Agile leadership"
    ]
  }
];

export const experiences: Experience[] = [
  {
    period: "Nov 2025 - Present",
    company: "Grid Dynamics",
    role: "Principal Delivery Manager",
    context: "Atlanta Metropolitan Area, on-site.",
    scope: [
      "Works across portfolio delivery, customer success, program management, and client relations.",
      "Applies enterprise solution delivery, Scaled Agile Framework, lean portfolio management, and P&L management skills."
    ]
  },
  {
    period: "Jul 2022 - Nov 2025",
    company: "Grid Dynamics",
    role: "Senior Service Delivery Manager",
    context: "Atlanta Metropolitan Area, on-site. Included multi-brand restaurant holding initiatives across UPD, IDP, DevOps, Platform, SRE, and Data Science.",
    scope: [
      "Managed cross-functional data engineering, big data, DevOps, platform, and data science teams.",
      "Owned roadmaps, milestones, executive communication, financial controls, and resource allocation.",
      "Led cloud architecture, data pipelines, machine learning models, CI/CD, data governance, and platform reliability initiatives.",
      "Built a high-performance culture through mentorship, collaboration, and leadership development."
    ]
  },
  {
    period: "Jan 2022 - Sep 2022",
    company: "Grid Dynamics",
    role: "Program Manager",
    context: "Global Grid Dynamics Internship program. Wroclaw, Dolnoslaskie, Poland.",
    scope: [
      "Led an internship program graduating 250+ interns annually.",
      "Managed planning, execution, conversion to full-time roles, P&L, budgeting, and cost control.",
      "Partnered with HR, technical teams, and marketing to build a reliable talent pipeline."
    ]
  },
  {
    period: "May 2021 - Jul 2022",
    company: "Grid Dynamics",
    role: "Delivery Manager",
    context: "Customer: e-commerce. Programs: Mobile, DevOps, NOC.",
    scope: [
      "Led development and operations teams from planning through delivery and operations.",
      "Controlled budget, scope, staffing, timelines, quality gates, vendor dependencies, and change management.",
      "Reported progress, risks, and strategic recommendations to executive sponsors."
    ]
  },
  {
    period: "03/2018 - 05/2021",
    company: "Dev.Pro",
    role: "Project Manager",
    context: "Customer: Global Payments. Programs: Xenial, Xenial Backoffice, Xenial Cloud.",
    scope: [
      "Established delivery practices across project and company contexts for enterprise restaurant technology.",
      "Managed stakeholder expectations around scope, service, cost, risk, and organizational impact.",
      "Organized planning with business teams and converted business ideas into governed delivery scope."
    ]
  },
  {
    period: "12/2016 - 03/2018",
    company: "Art Lemon Digital Agency",
    role: "Head of International Department",
    context: "International business development and dedicated team delivery.",
    scope: [
      "Led international business development strategy, market awareness, client networks, and delivery model evolution.",
      "Increased department sales and revenue by 500%.",
      "Introduced and implemented a dedicated team model while managing a team of 15 people."
    ]
  },
  {
    period: "11/2015 - 12/2016",
    company: "Art Lemon Digital Agency",
    role: "Project Manager",
    context: "Creative UI/UX, websites, e-commerce, SEO, and app development.",
    scope: [
      "Managed 50+ projects, including 10+ concurrent projects.",
      "Led team motivation, allocation, risk control, schedule management, client communication, and quality oversight.",
      "Worked across HTML, CSS, JavaScript, AngularJS, React, Node.js, PHP, WordPress, WooCommerce, OpenCart, Shopify, and Objective-C delivery contexts."
    ]
  }
];

export const earlierCareer = [
  "Strategic Partnerships Manager, Art Lemon Digital Agency, 11/2017 - 03/2018",
  "Regional Manager, PFK Terramet, 08/2015",
  "Commercial Sales and Acting Head of Grains and Oilseeds Purchase, SANGROUP, 08/2013 - 08/2015",
  "Commercial Sales, Agrostar Company, 01/2013 - 08/2013",
  "Sales Manager, PolymerAgro, 09/2012 - 12/2012"
];

export const education = [
  "Master's degree in International Economics, Kharkiv National University of Economics, 09/2006 - 02/2012",
  "SAFe 6 Practitioner, 2023",
  "PSM certified, 2021",
  "KMP certification preparation, 2021",
  "PSPO certification preparation, 2020",
  "PMP Exam Preparation from RMC, 2019",
  "Project management events and conferences: PMCon, PMDays, AgilePizza, PM Club, 2015 - 2019"
];

export const languages = ["English - C2", "Ukrainian - Native speaker", "Russian - Native speaker"];
